/* Step 12-1 (A0–A5) — the notes tab's new parts, kept out of pages/IFRS.jsx (near the 1,500-line rule for big files):
   - NoteFooter: under a note's table — the red difference line of a note that does not tie to the statements (A0.5:
     backend note_ties, ``note.tie_check``), and the sentence of an equity note that has no prior-year movements (A1);
   - NoteLedgerMovements: the movement table of the PPE / intangibles / lease-liability notes read from the ledger, or the
     sentence that says why it cannot be derived (A2: backend fs_note_movements_ledger, ``note.movements``);
   - cfNetLabel: the label of a cash-flow section's net cash, by its sign (A5: backend cf_net_label, ``net_label``).
   Figures come from the server; nothing is computed here. EN + AR: lib/i18n/2100_s12a_ties.js. */
import React from "react";
import { XCircle } from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";
import { fmtNumber } from "@/components/Amount";
import { asArray } from "@/lib/asArray";

const fmt = (n) => fmtNumber(Number(n ?? 0));

export function cfNetLabel(section, lang, fallback) {
  return (lang === "ar" && section?.net_label_ar) || section?.net_label || fallback;
}

export function noteTieFailed(note) {
  return note?.tie_check ? note.tie_check.status === "FAIL" : null;
}

export function NoteFooter({ note }) {
  const { t, lang } = useI18n();
  const tc = note?.tie_check;
  const failed = tc && tc.status === "FAIL";
  const prior = (lang === "ar" && note?.prior_note_ar) || note?.prior_note;
  if (!failed && !prior) return null;
  return (
    <div className="mt-1 space-y-1" data-testid={`note-footer-${note.note_no}`}>
      {prior && <p className="text-[11px] text-muted-foreground" data-testid={`note-prior-note-${note.note_no}`}>{prior}</p>}
      {failed && (
        <div className="rounded border border-red-200 bg-red-50 px-2 py-1 text-[11px] text-red-800 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300"
             data-testid={`note-tie-diff-${note.note_no}`}>
          <div className="flex items-center gap-1 font-semibold">
            <XCircle className="h-3 w-3" /> {t("s12a.notes.doesNotTie")} — {(lang === "ar" && tc.label_ar) || tc.label}
          </div>
          <ul className="ms-4 list-disc">
            {asArray(tc.targets).filter((x) => Math.abs(x.difference || 0) >= 0.005).map((x, i) => (
              <li key={i} data-testid={`note-tie-diff-${note.note_no}-${i}`}>
                {(lang === "ar" && x.line_ar) || x.line} · {t(x.year === "py" ? "s12a.notes.priorYear" : "s12a.notes.currentYear")}:{" "}
                {t("s12a.notes.noteFigure")} <span className="num-cell" dir="ltr">{fmt(x.note_figure)}</span> · {t("s12a.notes.statementFigure")}{" "}
                <span className="num-cell" dir="ltr">{fmt(x.statement_figure)}</span> · {t("s12a.notes.difference")}{" "}
                <span className="num-cell font-semibold" dir="ltr">{fmt(x.difference)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export function NoteLedgerMovements({ note }) {
  const { t, lang } = useI18n();
  const mv = note?.movements;
  if (!mv) return null;
  const cols = asArray(mv.columns);
  const lab = (x) => (lang === "ar" && x.label_ar) || x.label;
  return (
    <div className="mb-3 rounded border border-slate-200 bg-white p-2 dark:border-slate-800 dark:bg-slate-900/40"
         data-testid={`note-ledger-movements-${note.note_no}`}>
      <div className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-300">{t("s12a.movements.title")}</div>
      <p className={`mb-2 text-[11px] ${mv.derived ? "text-muted-foreground" : "text-amber-800 dark:text-amber-300"}`}
         data-testid={`note-ledger-movements-source-${note.note_no}`}>
        {mv.derived ? ((lang === "ar" && mv.source_text_ar) || mv.source_text) : ((lang === "ar" && mv.message_ar) || mv.message)}
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-[11px]">
          <thead>
            <tr className="text-[9px] uppercase tracking-wider text-muted-foreground">
              <th className="py-1 pe-2 text-start">{t("s12a.movements.line")}</th>
              <th className="py-1 pe-2 text-end">{t("s12a.movements.opening")}</th>
              {cols.map((c) => <th key={c.key} className="py-1 pe-2 text-end">{lab(c)}</th>)}
              <th className="py-1 pe-2 text-end">{t("s12a.movements.closing")}</th>
            </tr>
          </thead>
          <tbody>
            {asArray(mv.rows).map((r) => (
              <tr key={r.code} className="border-t border-slate-200/60 dark:border-slate-800/60" data-testid={`note-ledger-movement-row-${r.code}`}>
                <td className="py-1 pe-2">{(lang === "ar" && r.title_ar) || r.title || r.code}</td>
                <td className="py-1 pe-2 text-end num-cell">{fmt(r.opening)}</td>
                {cols.map((c) => <td key={c.key} className="py-1 pe-2 text-end num-cell">{r[c.key] == null ? "—" : fmt(r[c.key])}</td>)}
                <td className="py-1 pe-2 text-end num-cell font-semibold">{fmt(r.closing)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
